import { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { SliderWithTitle } from '@hivebrite/design-system/Slider';
import { toast } from '@hivebrite/design-system/Toast';

import { useLiveFeedContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/LiveFeedContext';
import { useTopPostsContext } from 'hb-react/frontoffice/apps/LiveFeed/contexts/TopPostsContext';
import Grid, { SliderRow } from 'hb-react/shared/components/DesignSystem/Grid';
import useOutboxedFontTheme from 'hb-react/shared/hooks/theme/useOutboxedFontTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { amplitudeLogger } from 'hb-react/shared/utils/amplitude';
import ThemeProvider from 'hb-react/style/ThemeProvider';

import LoadingCard from './LoadingCard';
import PostCardContainer from './PostCardContainer';

const TopPosts = () => {
  const outboxedFontTheme = useOutboxedFontTheme();
  const { amplitudeConfig } = useLiveFeedContext();
  const { isLoading, error, totalItems, topPosts, onLoadMore } =
    useTopPostsContext();
  const amplitude = amplitudeLogger(amplitudeConfig);
  const { flagUeA11YSliders } = useFeatureFlag();

  const onShowNext = () => {
    amplitude.track('LiveFeed|ScrolledThroughTopPosts');
  };

  if (error) {
    toast.error(__('Sorry, an error occurred'));

    return null;
  }

  if (!isLoading && topPosts.length === 0) {
    return null;
  }

  return (
    <Box
      flexDirection="column"
      alignItems="center"
      overflow="hidden"
      data-testid="top-posts"
    >
      <Box
        flexDirection="column"
        pt={6}
        mb={7}
        px={{
          _: 4,
          m: 0,
        }}
        width={{
          s: '100%',
          m: '597px',
          l: '960px',
          xl: '1144px',
        }}
      >
        {flagUeA11YSliders ? (
          <ThemeProvider
            theme="fo"
            fontBrightness={outboxedFontTheme.fontBrightness}
          >
            <Box flexDirection="column">
              <SliderWithTitle
                items={topPosts}
                total={totalItems}
                title={s__('LiveFeed|Top posts')}
                titleAs="h2"
                titleFont="h3-semibold"
                perPageBreakpoints={SLIDER_CARDS_PER_ROW}
                ItemComponent={PostCardContainer}
                LoadingItemComponent={LoadingCard}
                loading={isLoading}
                onLoadMore={onLoadMore}
                afterSlide={(index) => {
                  if (index > 0) {
                    onShowNext();
                  }
                }}
              />
            </Box>
          </ThemeProvider>
        ) : (
          <>
            <ThemeProvider
              theme="fo"
              fontBrightness={outboxedFontTheme.fontBrightness}
            >
              <Box as="h2" font="h3-semibold">
                {s__('LiveFeed|Top posts for you (%{totalItems})').i__({
                  totalItems: totalItems || 0,
                })}
              </Box>
            </ThemeProvider>
            <Grid
              width="100%"
              minWidth={0}
              mx={0}
              pl={0}
              mt="-4px" // remove a bit of the margin added by SliderRow
            >
              <SliderRow
                items={topPosts}
                total={totalItems}
                perPageBreakpoints={CARDS_PER_ROW}
                colWidthBreakpoints={CARDS_COL_WIDTH}
                ItemComponent={PostCardContainer}
                LoadingItemComponent={LoadingCard}
                onLoadMore={onLoadMore}
                onShowNext={onShowNext}
                customArrowStyle={ARROW_STYLE}
              />
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TopPosts;

const arrowStyle = css`
  top: 0;
  margin-top: 8px;
  height: 100%;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const bgColor = `245, 246, 246`;
const bgColorMaskGradient = `var(--color-background, rgb(${bgColor})) 25%, var(--color-background-transparent, rgba(${bgColor}, 0))`;

const leftArrowStyle = css`
  ${arrowStyle}
  padding-left: 8px;
  width: 100px;
  justify-content: flex-start;
  left: -4px;
  background: linear-gradient(to right, ${bgColorMaskGradient});
`;

const rightArrowStyle = css`
  ${arrowStyle}
  margin-right: -4px;
  padding-right: 8px;
  width: 130px;
  justify-content: flex-end;
  background: linear-gradient(to left, ${bgColorMaskGradient});
`;

const ARROW_STYLE = {
  leftArrowStyle,
  rightArrowStyle,
  isSmall: true,
};

const SLIDER_CARDS_PER_ROW = {
  s: 1,
  m: 3,
  l: 4,
  xl: 4,
};

const CARDS_PER_ROW = {
  s: 1,
  m: 2,
  l: 3,
  xl: 3,
};

const CARDS_COL_WIDTH = {
  s: 4 / 5,
  m: 2 / 5,
  l: 3 / 10,
  xl: 3 / 10,
};
